import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { eventsApi } from '@/features/events/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { useT } from '@/lib/i18n/useT';

interface ResponsesTabProps {
  stationId: number;
  surveyId: number | null;
}

const PAGE_SIZE = 20;

export function ResponsesTab({ stationId, surveyId }: ResponsesTabProps) {
  const t = useT();
  const [page, setPage] = useState(1);

  // Without a survey assigned the grouped endpoint can't filter — nothing to
  // show. We render an explicit hint instead of an empty table.
  const query = useQuery({
    queryKey: queryKeys.stations.responses(stationId, page),
    queryFn: () =>
      eventsApi.listStationResponses({
        surveyId: surveyId!,
        stationId,
        page,
        limit: PAGE_SIZE,
      }),
    enabled: !!surveyId,
  });

  if (!surveyId) {
    return (
      <Card>
        <CardContent>
          <p className="py-12 text-center text-sm text-[var(--color-fg-muted)]">
            {t('stations.detail.noSurveyAssigned')}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (query.isLoading) {
    return (
      <Card>
        <CardContent className="space-y-2 py-4">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </CardContent>
      </Card>
    );
  }

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (items.length === 0) {
    return (
      <Card>
        <CardContent>
          <p className="py-12 text-center text-sm text-[var(--color-fg-muted)]">
            {t('stations.detail.noResponses')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('stations.detail.responses')}{' '}
          <span className="text-sm font-normal text-[var(--color-fg-muted)]">
            ({total})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-[var(--color-fg-subtle)]">
              <tr>
                <th className="py-2 text-left">
                  {t('stations.detail.responseTimestamp')}
                </th>
                <th className="py-2 text-left">
                  {t('stations.detail.responseSummary')}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                // Show first two answers as a quick summary preview.
                const preview = (row.answers ?? [])
                  .slice(0, 2)
                  .map((a) => `${a.label ?? '—'}: ${a.value ?? '—'}`)
                  .join(' · ');
                return (
                  <tr
                    key={row.responseId}
                    className="border-t border-[var(--color-border-subtle)]"
                  >
                    <td className="py-3 align-top tabular-nums text-[var(--color-fg-muted)]">
                      {row.submittedAt
                        ? new Date(row.submittedAt).toLocaleString()
                        : '—'}
                    </td>
                    <td className="py-3 text-[var(--color-fg-primary)]">
                      {preview || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-end gap-2">
            <span className="text-xs text-[var(--color-fg-muted)]">
              {page} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label={t('stations.detail.prevPage')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              aria-label={t('stations.detail.nextPage')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
